/**
 * CollapsingMultiSelect
 *
 * A thin wrapper around Mantine `MultiSelect` that DYNAMICALLY collapses the
 * selected pills based on available width: while every selected pill fits on a
 * single input line they render normally; once the full set would overflow that
 * line the input collapses to a single "Wybrano X" summary pill (X = number of
 * selected values). The decision is a real width measurement (see
 * `useCollapsedPills`), not a count threshold — so it adapts to label length and
 * input width.
 *
 * Built ON Mantine primitives only (MultiSelect + Pill); the wrapper owns the
 * `renderPill` collapse logic and the hidden measurer, and forwards the relevant
 * MultiSelect props through unchanged.
 */
import { memo, useCallback, useMemo } from 'react';
import { MultiSelect, Pill } from '@mantine/core';
import type { ComboboxRenderPillInput, MultiSelectProps } from '@mantine/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useCollapsedPills } from './useCollapsedPills';
import styles from './CollapsingMultiSelect.module.css';

/**
 * Stable class applied to the MultiSelect `pillsList` part — the single source
 * for both the `classNames` override and the measurement query selector. The
 * `?? ''` guards `noUncheckedIndexedAccess` widening the CSS-module lookup; the
 * hook treats an empty selector as "no container" and skips measuring.
 */
const PILLS_LIST_CLASS = styles.pillsList ?? '';

/**
 * Props this wrapper owns internally and therefore does NOT forward to the
 * underlying `MultiSelect` (it drives `renderPill`/`classNames` itself).
 */
type WrapperOwnedProps = 'renderPill' | 'classNames';

/**
 * All MultiSelect props (so the documented subset — `data`, `value`, `onChange`,
 * `label`, `placeholder`, `leftSection`, `clearable`, `searchable`,
 * `comboboxProps`, `className`, `renderOption`, `nothingFoundMessage` — and any
 * other standard MultiSelect prop are forwarded verbatim), minus the parts the
 * wrapper controls, plus the single `renderCount` addition. One spread prop on
 * the component keeps the explicit surface within the size budget.
 */
export interface CollapsingMultiSelectProps extends Omit<
  MultiSelectProps,
  WrapperOwnedProps
> {
  /**
   * Builds the collapsed summary label from the selected count.
   * Defaults to `t('filters.selectedCount', { count })`.
   */
  renderCount?: (count: number) => string;
}

/** Resolves an option value to its display label using the data map. */
function buildLabelMap(data: MultiSelectProps['data']): Map<string, string> {
  const labelMap = new Map<string, string>();
  if (!data) {
    return labelMap;
  }
  for (const entry of data) {
    if (typeof entry === 'string') {
      labelMap.set(entry, entry);
    } else if ('value' in entry) {
      labelMap.set(entry.value, entry.label ?? entry.value);
    }
    // Grouped option entries ({ group, items }) are not used by the filter
    // call sites this component serves; they are intentionally skipped.
  }
  return labelMap;
}

function CollapsingMultiSelectComponent({
  data,
  value,
  className,
  renderCount,
  placeholder,
  ...multiSelectProps
}: CollapsingMultiSelectProps) {
  const { t } = useTranslation();

  const selectedValues = useMemo(() => value ?? [], [value]);
  const labelMap = useMemo(() => buildLabelMap(data), [data]);

  // `searchable` stays inside `multiSelectProps` (still forwarded); we only read
  // it to toggle the focus-to-search styling on the wrapper.
  const isSearchable = Boolean(multiSelectProps.searchable);
  const hasSelection = selectedValues.length > 0;

  const { collapsed, wrapperRef, measurerRef } = useCollapsedPills(
    selectedValues.length,
    PILLS_LIST_CLASS
  );

  const resolveCountLabel = useCallback(
    (count: number) =>
      renderCount ? renderCount(count) : t('filters.selectedCount', { count }),
    [renderCount, t]
  );

  const renderPill = useCallback(
    ({ option, onRemove }: ComboboxRenderPillInput<string>) => {
      if (!option) {
        return null;
      }
      const optionValue = String(option.value);
      const optionLabel = labelMap.get(optionValue) ?? option.label;

      if (!collapsed) {
        return (
          <Pill className={styles.pill} withRemoveButton onRemove={onRemove}>
            {optionLabel}
          </Pill>
        );
      }

      // Collapsed: render the summary only on the first selected value; the
      // remaining values render nothing so the input stays single-line.
      if (optionValue !== selectedValues[0]) {
        return null;
      }
      return (
        <Pill className={styles.countPill}>
          {resolveCountLabel(selectedValues.length)}
        </Pill>
      );
    },
    [collapsed, labelMap, resolveCountLabel, selectedValues]
  );

  return (
    <div
      className={clsx(
        styles.root,
        className,
        isSearchable && styles.searchable,
        hasSelection && styles.hasSelection
      )}
      ref={wrapperRef}
    >
      <MultiSelect
        {...multiSelectProps}
        data={data}
        value={value}
        placeholder={hasSelection ? undefined : placeholder}
        renderPill={renderPill}
        classNames={{
          pillsList: PILLS_LIST_CLASS,
          // Collapses to zero width when blurred WITH a selection so a single
          // pill never leaves an empty trailing row (see `.searchField` in CSS).
          // The `?? ''` guards `noUncheckedIndexedAccess` on the module lookup.
          inputField: styles.searchField ?? '',
        }}
      />
      <div className={styles.measurer} ref={measurerRef} aria-hidden>
        {selectedValues.map((selectedValue) => (
          <Pill key={selectedValue} withRemoveButton>
            {labelMap.get(selectedValue) ?? selectedValue}
          </Pill>
        ))}
      </div>
    </div>
  );
}

export const CollapsingMultiSelect = memo(CollapsingMultiSelectComponent);
